/**
 * NewArtifactPage — /artifacts/new
 * Demo page: /form-layouts (sectioned form with a side card) on the FormPage template; the aside is Field Help.
 * Gated by CanCreateMemoriesArtifacts (POST /artifacts).
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useWatch } from 'react-hook-form';
import {
  Form,
  FormActions,
  FormFullWidth,
  FormSection,
  SelectFormField,
  TextAreaFormField,
  TextFormField,
  useZodForm,
} from '../../components/form/index.js';
import { useToast } from '../../components/ui/index.js';
import { FormPage } from '../../components/page/index.js';
import { Can } from '../../components/auth/Can.js';
import { PAGE_HELP } from '../../help/index.js';
import { ARTIFACTS_FIELD_HELP } from '../../help/artifacts-fields.js';
import {
  createArtifactMutation,
  listArtifactsInfiniteQueryKey,
} from '../../generated/api/@tanstack/react-query.gen.js';
import { zArtifactCreate } from '../../generated/api/zod.gen.js';
import { FieldHelpAside, HelpField, useFocusedHelpField } from './entity-ui/FieldHelpAside.js';
import { ARTIFACT_KINDS, getKindConfig, type ArtifactScope } from './artifact-detail/kind-config.js';
import { MEMORY_SCOPE_LABEL, MEMORY_SCOPES } from './memory-detail/memory-scope.js';

/** The subject type each scope implies; only the agent scope offers a choice. */
const SCOPE_SUBJECT_TYPE: Record<Exclude<ArtifactScope, 'agent'>, string> = {
  conversation: 'conversation_id',
  team: 'team_id',
  task: 'task_id',
  project: 'project_id',
  org: 'organization_id',
};

const AGENT_SUBJECT_TYPES = [
  { value: 'agent_template_id', label: 'Agent template' },
  { value: 'daemon_id', label: 'Employee' },
];

const FIELD_HELP: Record<string, string> = {
  ...ARTIFACTS_FIELD_HELP,
  subjectId: 'The id of the conversation, project, task, agent, team or organization this artifact belongs to.',
  subjectType: 'For the agent scope, say whether the id is an agent template or an employee.',
};

const createSchema = zArtifactCreate.extend({
  title: zArtifactCreate.shape.title.min(1).max(200),
  subjectId: zArtifactCreate.shape.subjectId.min(1),
  content: zArtifactCreate.shape.content.min(1),
});

const LIST_PATH = '/artifacts';

export const NewArtifactPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const create = useMutation(createArtifactMutation());
  const { focusedField, onFocus } = useFocusedHelpField();

  const form = useZodForm(createSchema, {
    defaultValues: {
      kind: 'report',
      title: '',
      scope: 'conversation',
      subjectType: 'agent_template_id',
      subjectId: '',
      content: '',
    },
  });
  const scope = useWatch({ control: form.control, name: 'scope' });

  const submit = async (values: ReturnType<typeof createSchema.parse>) => {
    const subjectType = values.scope === 'agent' ? values.subjectType : SCOPE_SUBJECT_TYPE[values.scope];
    const created = await create.mutateAsync({ body: { ...values, subjectType, subjectId: values.subjectId.trim() } });
    await queryClient.invalidateQueries({ queryKey: listArtifactsInfiniteQueryKey() });
    notify('Artifact created', 'success');
    navigate(created?.id ? `${LIST_PATH}/${created.id}` : LIST_PATH);
  };

  return (
    <Can policy="CanCreateMemoriesArtifacts">
      <FormPage
        title="New artifact"
        subtitle="Save a plan, report, spec or any document so agents and people can find it."
        breadcrumbs={[{ label: 'Artifacts', to: LIST_PATH }]}
        helpContent={PAGE_HELP['artifacts']}
        backTo={LIST_PATH}
        backLabel="Artifacts"
        aside={
          <FieldHelpAside
            helpMap={FIELD_HELP}
            labels={{ subjectId: 'Belongs to', subjectType: 'Belongs to (type)' }}
            focusedField={focusedField}
            overviewTitle="Artifacts"
            overviewBody="Artifacts are the durable outputs of work: plans, reports, specs, code and decisions. Every artifact belongs to one conversation, project, task, agent, team or organization."
            assistant={
              <p className="text-sm text-muted">
                You can also ask an agent in a conversation to write an artifact for you.
              </p>
            }
          />
        }
      >
        <Form form={form} onSubmit={submit} onFocus={onFocus} guardUnsaved>
          <FormSection title="Artifact">
            <HelpField name="kind">
              <SelectFormField
                name="kind"
                label="Type"
                required
                help={ARTIFACTS_FIELD_HELP.kind}
                options={ARTIFACT_KINDS.map((k) => ({ value: k, label: getKindConfig(k).badgeLabel }))}
              />
            </HelpField>
            <HelpField name="title">
              <TextFormField
                name="title"
                label="Title"
                required
                help={ARTIFACTS_FIELD_HELP.title}
                placeholder="Decision: increase position size by 10%"
              />
            </HelpField>
            <HelpField name="scope">
              <SelectFormField
                name="scope"
                label="Scope"
                required
                help={ARTIFACTS_FIELD_HELP.scope}
                options={MEMORY_SCOPES.map((s) => ({ value: s, label: MEMORY_SCOPE_LABEL[s] }))}
              />
            </HelpField>
            {scope === 'agent' && (
              <HelpField name="subjectType">
                <SelectFormField
                  name="subjectType"
                  label="Belongs to (type)"
                  required
                  help={FIELD_HELP.subjectType}
                  options={AGENT_SUBJECT_TYPES}
                />
              </HelpField>
            )}
            <HelpField name="subjectId">
              <TextFormField
                name="subjectId"
                label="Belongs to"
                required
                help={FIELD_HELP.subjectId}
                placeholder="Paste the id"
              />
            </HelpField>
            <FormFullWidth>
              <HelpField name="content">
                <TextAreaFormField
                  name="content"
                  label="Content"
                  required
                  rows={16}
                  help={ARTIFACTS_FIELD_HELP.content}
                  description="Markdown is supported."
                />
              </HelpField>
            </FormFullWidth>
          </FormSection>
          <FormActions submitLabel="Create artifact" cancelTo={LIST_PATH} />
        </Form>
      </FormPage>
    </Can>
  );
};
